import { useCallbackRef, Text, Flex, InputGroup, useRadioGroup, Box } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import OptionComponent from '../OptionComponent';
import { DateFilterInputValue, DateRangeFilterFieldsProps, transalateDateFilterInputValue } from './types';
import { mapDateValueToDateRange } from './utils/mapDateValueToDateRange';
import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale/pt-BR';
import { formatDateIfValid } from '@/utils/formatDateIfValid';
import { formatedDateToDate, getDateFormatByLanguageToDate } from '@/utils/date';
import { useTranslation } from 'react-i18next';
import { t } from '@/i18n';

registerLocale('pt-BR', ptBR);
setDefaultLocale('pt-BR');

const DEFAULT_DATE_VALUE = DateFilterInputValue.ALL;

export const DateRangeFilterFields = ({
  dateSelectLabel = t('components.date_range_filter_fields.date_range_filter_fields_start_date_label'),
  startDate: startDateProp,
  endDate: endDateProp,
  dateValue: dateValueProp,
  onDateValueChange: onDateValueChangeProp,
  onRangeChange: onRangeChangeProp,
  startDateRootProps,
  endDateRootProps,
  containerProps,
}: DateRangeFilterFieldsProps) => {
  const { t } = useTranslation();

  const [dateValue, setDateValue] = useState(dateValueProp ?? DEFAULT_DATE_VALUE);
  const [startDate, setStartDate] = useState(startDateProp ?? '');
  const [endDate, setEndDate] = useState(endDateProp ?? '');
  const [isDefining, setIsDefining] = useState(dateValue === DateFilterInputValue.DEFINE_DATE);

  const onRangeChange = useCallbackRef(onRangeChangeProp);
  const onDateValueChange = useCallbackRef(onDateValueChangeProp);

  const isRangeDefined = useMemo(() => (startDate && endDate) || (!startDate && !endDate), [endDate, startDate]);

  const { getRootProps, getRadioProps, setValue } = useRadioGroup({
    name: 'date-range',
    defaultValue: DEFAULT_DATE_VALUE,
    onChange: value => setDateValue(value as DateFilterInputValue),
  });

  const group = getRootProps();

  useEffect(() => {
    setDateValue(dateValueProp ?? DEFAULT_DATE_VALUE);
    setIsDefining(dateValueProp === DateFilterInputValue.DEFINE_DATE);
    setValue(dateValueProp ?? DEFAULT_DATE_VALUE);
  }, [dateValueProp, setValue]);

  useEffect(() => {
    setStartDate(startDateProp ?? '');
  }, [startDateProp]);

  useEffect(() => {
    setEndDate(endDateProp ?? '');
  }, [endDateProp]);

  useEffect(() => {
    setIsDefining(dateValue === DateFilterInputValue.DEFINE_DATE);
    onDateValueChange?.(dateValue);

    const dateRange = mapDateValueToDateRange(dateValue);
    setStartDate(dateRange.startDate);
    setEndDate(dateRange.endDate);
  }, [dateValue, onDateValueChange]);

  useEffect(() => {
    if (!isRangeDefined || isDefining) {
      return;
    }

    onRangeChange?.(startDate, endDate);
  }, [startDate, endDate, isDefining, onRangeChange, isRangeDefined]);

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'flex-end' }}
      wrap='wrap'
      gap={4}
      w='100%'
      flex='1 1 auto'
      {...containerProps}
    >
      <Box w={{ base: '100%', md: 'fit-content' }}>
        <Text fontWeight='medium' mb={2}>
          {dateSelectLabel}
        </Text>
        <Flex {...group} wrap='wrap' gap={2} overflow='visible' justify={{ base: 'flex-start', md: 'start' }}>
          {Object.entries(DateFilterInputValue)
            .filter(([, value]) => value !== DateFilterInputValue.ALL)
            .map(([, value]) => {
              const radio = getRadioProps({ value });
              return (
                <OptionComponent key={value} {...radio}>
                  {transalateDateFilterInputValue(value)}
                </OptionComponent>
              );
            })}
        </Flex>
      </Box>

      {isDefining && (
        <>
          <InputGroup w={{ base: '100%', md: '200px' }} display='flex' flexDirection='column' {...startDateRootProps}>
            <Text fontWeight='medium' mb={1}>
              {t('components.date_range_filter_fields.date_range_filter_fields_start_date_label')}
            </Text>
            <DatePicker
              popperClassName='datepicker-zindex-fix'
              showIcon
              selected={formatedDateToDate(startDate)}
              onChange={(date: Date) => {
                const formatted = formatDateIfValid(date, {
                  formatTemplate: 'YYYY-MM-DD',
                  fallbackValue: '',
                });
                setStartDate(formatted);
                onRangeChange(formatted, endDate);
              }}
              toggleCalendarOnIconClick
              dateFormat={getDateFormatByLanguageToDate().format}
              placeholderText={getDateFormatByLanguageToDate().placeholder}
            />
          </InputGroup>

          <InputGroup w={{ base: '100%', md: '200px' }} display='flex' flexDirection='column' {...endDateRootProps}>
            <Text fontWeight='medium' mb={1}>
              {t('components.date_range_filter_fields.date_range_filter_fields_end_date_label')}
            </Text>
            <DatePicker
              popperClassName='datepicker-zindex-fix'
              showIcon
              selected={formatedDateToDate(endDate)}
              onChange={(date: Date) => {
                const formatted = formatDateIfValid(date, {
                  formatTemplate: 'YYYY-MM-DD',
                  fallbackValue: '',
                });
                setEndDate(formatted);
                onRangeChange(startDate, formatted);
              }}
              toggleCalendarOnIconClick
              dateFormat={getDateFormatByLanguageToDate().format}
              placeholderText={getDateFormatByLanguageToDate().placeholder}
            />
          </InputGroup>
        </>
      )}
    </Flex>
  );
};
